import { ColDef, ColGroupDef } from 'ag-grid-community';
import { Job } from 'ldt-people-api';
import { betterDateFilterParams } from './people-preview-grid-settings';
import * as moment from 'moment';

export const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  flex: 1,
  minWidth: 100,
  suppressHeaderMenuButton: true,
};

export const peopleFullGridSettings: (ColDef | ColGroupDef)[] = [
  {
    headerName: 'Person Info',
    children: [
      { field: 'name' },
      { field: 'country' },
      { field: 'location' },
      {
        field: 'linkedin',
        cellRenderer: (params: { value: any }) => {
          if (!params.value) {
            return '';
          } else {
            return (
              '<a href="https://www.linkedin.com/in/' +
              params.value +
              '" target=_blank>' +
              params.value +
              '</a>'
            );
          }
        },
        headerTooltip:
          'The LinkedIn ID of this person. Always uses the customer-provide ID, if provided. If not provided, ID was discovered by Live Data.',
      },
    ],
  },
  {
    headerName: 'Current Job',
    children: [
      { field: 'position.company.name', headerName: 'Company Name' },
      {
        field: 'position.title',
        headerName: 'Title',
        headerTooltip: 'The verified title this person currently holds',
      },
      {
        field: 'position.level',
        headerName: 'Job Level',
        headerTooltip: 'The inferred level of the current job, based on the title.',
      },
      {
        field: 'position.function',
        headerName: 'Job Function',
        headerTooltip: 'The inferred domain of the current job, based on the title.',
      },
      {
        field: 'position.started_at',
        headerName: 'Start Date',
        headerTooltip: 'The date the person started at their current job',
        filter: 'agDateColumnFilter',
        valueFormatter: (params: any) => {
          if (!params?.data?.position?.started_at) return '';

          if (params?.data?.position?.metadata?.started_at_year_only)
            return !params?.data?.position?.started_at.substr(0, 4);

          return params?.data?.position?.started_at.substr(0, 10);
        },
      },
      { field: 'position.location', headerName: 'Job Location' },
    ],
  },
  {
    headerName: 'Company Firmo',
    children: [
      {
        field: 'position.company.linkedin',
        headerName: 'LinkedIn',
        cellRenderer: (params: { value: any }) => {
          if (!params.value) {
            return '';
          } else {
            return (
              '<a href="https://www.linkedin.com/company/' +
              params.value +
              '" target=_blank>' +
              params.value +
              '</a>'
            );
          }
        },
      },
      { field: 'position.company.domain', headerName: 'Domain' },
      { field: 'position.company.ticker', headerName: 'Ticker' },

      { field: 'position.company.country', headerName: 'Country' },
      {
        field: 'position.company.employee_count',
        headerName: 'Employees',
      },
      { field: 'position.company.industry', headerName: 'Industry' },

      { field: 'position.company.location', headerName: 'Location' },
      {
        field: 'position.company.type',
        headerName: 'Type',
      },
    ],
  },
  {
    headerName: 'Previous Job - searching these fields search ALL previous jobs',
    children: [
      {
        valueGetter: (params: any) => params?.data?.jobs[1]?.company.name,
        headerName: 'Company Name',
        field: 'jobs.company.name',
        sortable: false,
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[1]?.title,
        headerName: 'Title',
        field: 'jobs.title',
        sortable: false,
        headerTooltip: 'The verified title of this person at this job',
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[1]?.level,
        headerName: 'Job Level',
        field: 'jobs.level',
        sortable: false,
        filterParams: { values: Object.values(Job.LevelEnum) },
        filter: 'agSetColumnFilter',
        headerTooltip: 'The inferred level of the job, based on the title.',
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[1]?.function,
        headerName: 'Job Function',
        field: 'jobs.function',
        sortable: false,
        filterParams: { values: Object.values(Job.FunctionEnum) },
        filter: 'agSetColumnFilter',
        headerTooltip: 'The inferred domain of the job, based on the title.',
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[1]?.started_at,
        headerName: 'Start Date',
        filter: 'agDateColumnFilter',
        field: 'jobs.started_at',
        sortable: false,
        headerTooltip: 'The date the person started at this job',
        valueFormatter: (params: any) => {
          let tString = 'yyyy-MM';
          if (params?.data?.jobs[1]?.metadata?.started_at_year_only) tString = 'yyyy';
          return moment(params?.data?.jobs[1]?.started_at).format(tString) || '';
        },
        filterParams: betterDateFilterParams,
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[1]?.ended_at,
        headerName: 'End Date',
        filter: 'agDateColumnFilter',
        field: 'jobs.ended_at',
        sortable: false,
        headerTooltip: 'The date the person ended at this job',
        valueFormatter: (params: any) => {
          let tString = 'yyyy-MM';
          if (params?.data?.jobs[1]?.metadata?.ended_at_year_only) tString = 'yyyy';
          return moment(params?.data?.jobs[1]?.ended_at).format(tString) || '';
        },
        filterParams: betterDateFilterParams,
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[1]?.location,
        headerName: 'Job Location',
        field: 'jobs.company.location',
        sortable: false,
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[1]?.company.linkedin,
        headerName: 'Company LinkedIn',
        field: 'jobs.company.linkedin',
        sortable: false,
        cellRenderer: (params: { value: any }) => {
          if (!params.value) {
            return '';
          } else {
            return (
              '<a href="https://www.linkedin.com/company/' +
              params.value +
              '" target=_blank>' +
              params.value +
              '</a>'
            );
          }
        },
      },
    ],
  },
  {
    headerName: 'Previous Job - 2',
    children: [
      {
        valueGetter: (params: any) => params?.data?.jobs[2]?.company.name,
        headerName: 'Company Name',
        filter: false,
        sortable: false,
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[2]?.title,
        headerName: 'Title',
        filter: false,
        sortable: false,
        headerTooltip: 'The verified title of this person at this job',
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[2]?.level,
        headerName: 'Job Level',
        filter: false,
        sortable: false,
        headerTooltip: 'The inferred level of the job, based on the title.',
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[2]?.function,
        headerName: 'Job Function',
        filter: false,
        sortable: false,
        headerTooltip: 'The inferred domain of the job, based on the title.',
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[2]?.started_at,
        headerName: 'Start Date',
        filter: false,
        sortable: false,
        headerTooltip: 'The date the person started at this job',
        valueFormatter: (params: any) => {
          let tString = 'yyyy-MM';
          if (params?.data?.jobs[2]?.metadata?.started_at_year_only) tString = 'yyyy';
          return moment(params?.data?.jobs[2]?.started_at).format(tString) || '';
        },
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[2]?.ended_at,
        headerName: 'End Date',
        filter: false,
        sortable: false,
        headerTooltip: 'The date the person ended at this job',
        valueFormatter: (params: any) => {
          let tString = 'yyyy-MM';
          if (params?.data?.jobs[2]?.metadata?.ended_at_year_only) tString = 'yyyy';
          return moment(params?.data?.jobs[2]?.ended_at).format(tString) || '';
        },
        filterParams: betterDateFilterParams,
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[2]?.location,
        headerName: 'Job Location',
        filter: false,
        sortable: false,
      },
      {
        valueGetter: (params: any) => params?.data?.jobs[2]?.company.linkedin,
        headerName: 'Company LinkedIn',
        filter: false,
        sortable: false,
        cellRenderer: (params: { value: any }) => {
          if (!params.value) {
            return '';
          } else {
            return (
              '<a href="https://www.linkedin.com/company/' +
              params.value +
              '" target=_blank>' +
              params.value +
              '</a>'
            );
          }
        },
      },
    ],
  },
];
